import { Bell, BellOff, Download, ExternalLink, Upload } from "lucide-react";
import type { Resource, ResourceCategory } from "../types";
import { ResourceTypeBadge } from "./ResourceTypeBadge";
import { ResourceDetailModal } from "./ResourceDetailModal";
import { useState } from "react";
import { useToggleSubscribe } from "../hooks/useRepository";

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("pl-PL", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
}

interface Props {
  resource: Resource;
  categories: ResourceCategory[];
}

export function ResourceCard({ resource, categories }: Props) {
  const isLink = resource.resource_type === "link";

  const { mutateAsync: toggleSub, isPending: toggling } = useToggleSubscribe();
  const [showDetail, setShowDetail] = useState(false);

  return (
    <>
      <div
        className="flex flex-col gap-2 p-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-[var(--color-primary)] dark:hover:border-[var(--color-primary)] hover:shadow-sm transition-all cursor-pointer group"
        onClick={() => setShowDetail(true)}
      >
        {/* Header row */}
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <ResourceTypeBadge type={resource.resource_type} />
            <h3 className="mt-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100 truncate group-hover:text-[var(--color-primary)] dark:group-hover:text-[var(--color-primary)] transition-colors">
              {resource.title}
            </h3>
          </div>

          {/* Bell — stop propagation so clicking it doesn't open modal */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleSub({ id: resource.id, subscribed: resource.is_subscribed });
            }}
            disabled={toggling}
            title={resource.is_subscribed ? "Wyłącz powiadomienia" : "Włącz powiadomienia"}
            className="flex-shrink-0 p-1 rounded text-gray-300 hover:text-[var(--color-primary)] dark:hover:text-[var(--color-primary)] transition-colors"
          >
            {resource.is_subscribed
              ? <Bell size={14} className="text-[var(--color-primary)]" />
              : <BellOff size={14} />
            }
          </button>
        </div>

        {/* Description */}
        {resource.description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{resource.description}</p>
        )}

        {/* Bottom row */}
        <div className="flex items-center justify-between gap-2 mt-auto pt-1 border-t border-gray-100 dark:border-gray-700">
          <span className="text-xs text-gray-400">{resource.category_name}</span>

          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {isLink && resource.url && (
              <a
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-[var(--color-primary)] dark:text-[var(--color-primary)] hover:underline"
              >
                <ExternalLink size={11} />
                Otwórz
              </a>
            )}

            {!isLink && resource.latest_version && (
              <a
                href={resource.latest_version.url}
                download={resource.latest_version.original_name}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-[var(--color-primary)] dark:text-[var(--color-primary)] hover:underline"
              >
                <Download size={11} />
                v{resource.latest_version.version_number} · Pobierz
              </a>
            )}

            {!isLink && !resource.latest_version && resource.can_manage && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <Upload size={11} />
                Brak pliku
              </span>
            )}
          </div>
        </div>

        {resource.latest_version && !isLink && (
          <p className="text-xs text-gray-400">
            Aktualizacja: {formatDate(resource.latest_version.created_at)}
            {resource.latest_version.uploaded_by_name && ` · ${resource.latest_version.uploaded_by_name}`}
          </p>
        )}
      </div>

      {showDetail && (
        <ResourceDetailModal
          resource={resource}
          categories={categories}
          onClose={() => setShowDetail(false)}
        />
      )}
    </>
  );
}
